import { useState, useEffect, useRef, useCallback } from 'react';
import { getProgress, startFullProcessing, getDownloadLinks } from '@/utils/api';
import { useToast } from '@/hooks/use-toast';

// 🚀 Map schema mới -> shape cũ mà UI đang kỳ vọng
const computeStatus = (p: any): string => {
  // 🚀 FIX: Chỉ return 'completed' khi thực sự hoàn thành
  if (p?.full?.status === 'completed') return 'completed';
  
  // 🚀 FIX: Nếu full đang processing, KHÔNG return 'completed'
  if (p?.full?.status === 'processing') return 'processing';
  
  // 🚀 FIX: Preview completed chỉ khi full không processing
  if (p?.preview?.status === 'completed' && p?.full?.status !== 'processing') return 'completed';
  
  if (p?.preview?.error_message) return 'error';
  if (p?.full?.error_message) return 'error';
  
  if (p?.preview?.progress === 100) return 'processing';
  if (p?.full?.progress === 100) return 'processing';
  return 'processing';
};

export interface VideoData {
  videoId: string;
  jobId: string;
  preview?: {
    status: string;
    progress: number;
    stage?: string;
    s3_srt_url?: string;
    s3_vtt_url?: string;  // 🚀 NEW: VTT URL
    s3_video_url?: string;
    duration?: number;
    error_message?: string;
  };
  full?: {
    status: string;
    progress: number;
    stage?: string;
    job_id?: string;  // 🚀 ADD: job_id for full processing
    s3_srt_url?: string;
    s3_vtt_url?: string;  // 🚀 NEW: VTT URL
    s3_video_url?: string;
    duration?: number;
    error_message?: string;
  };
}

// Check if status is terminal (should stop polling)
const isTerminal = (dataOrStatus: any): boolean => {
  // Nếu truyền vào là string (đã map) thì dùng luôn, còn không thì map từ object thô
  const status = typeof dataOrStatus === 'string' ? dataOrStatus : computeStatus(dataOrStatus ?? {});
  
  console.log('🔍 isTerminal debug:', {
    input: dataOrStatus,
    input_type: typeof dataOrStatus,
    computed_status: status,
    is_string_terminal: ['completed', 'error'].includes(status)
  });
  
  if (['completed', 'error'].includes(status)) return true;

  // 🚀 FIX: Check preview.status = 'error' immediately to stop polling
  if (dataOrStatus?.preview?.status === 'error') {
    console.log('❌ isTerminal - Preview status is error, stopping poll immediately');
    return true;
  }

  // 🚀 FIX: Check full.status = 'error' immediately to stop polling
  if (dataOrStatus?.full?.status === 'error') {
    console.log('❌ isTerminal - Full status is error, stopping poll immediately');
    return true;
  }

  // fallback khi truyền object thô - CHỈ CHECK TRONG FULL VÀ PREVIEW
  const d = typeof dataOrStatus === 'string' ? undefined : dataOrStatus;
  if (!d) return false;
  
  // 🚀 LOGIC MỚI: Chỉ check trong full và preview scope
  if (d.full) {
    // Nếu full có video URL -> dừng poll
    if (d.full.s3_video_url || d.full.s3_srt_url) {
      console.log('✅ isTerminal - Full has video URL, stopping poll');
      return true;
    }
    
    // Nếu full có error_message -> dừng poll
    if (d.full.error_message) {
      console.log('❌ isTerminal - Full has error message, stopping poll');
      return true;
    }
    
    // Nếu full.status = "completed" -> dừng poll
    if (d.full.status === 'completed') {
      console.log('✅ isTerminal - Full status completed, stopping poll');
      return true;
    }
    
    // 🚀 FIX: Nếu full.status = "processing" -> TIẾP TỤC poll
    if (d.full.status === 'processing') {
      console.log('⏳ isTerminal - Full status processing, continue polling');
      return false; // Tiếp tục poll
    }
    
    // Nếu full.status = "not_started" -> check preview
    if (d.full.status === 'not_started') {
      console.log('⏳ isTerminal - Full not started, checking preview...');
      
      // 🚀 FIX: Chỉ dừng poll nếu preview completed VÀ không cần full processing
      // Nếu preview completed nhưng full chưa start -> TIẾP TỤC poll để đợi full
      if (d.preview?.status === 'completed') {
        // 🚀 FIX: Chỉ dừng poll nếu full đã hoàn thành hoặc có lỗi
        if (d.full?.status === 'completed' || d.full?.error_message) {
          console.log('✅ isTerminal - Preview completed and full finished, stopping poll');
          return true;
        }
        
        // 🚀 FIX: Nếu preview done nhưng full chưa start -> TIẾP TỤC poll
        console.log('⏳ isTerminal - Preview completed but full not started, continue polling for full');
        return false;
      }
      
      // Nếu preview có URL -> dừng poll để show preview video
      if (d.preview?.s3_video_url || d.preview?.s3_srt_url) {
        console.log('✅ isTerminal - Preview has video URL, stopping poll to show preview video');
        return true;
      }
      
      // Nếu preview có error -> dừng poll
      if (d.preview?.error_message) {
        console.log('❌ isTerminal - Preview has error message, stopping poll');
        return true;
      }
    }
  }
  
  // 🚀 FIX: Nếu không có full, check preview nhưng đừng dừng poll quá sớm
  if (d.preview && !d.full) {
    // Nếu preview có video URL -> dừng poll để show preview video
    if (d.preview.s3_video_url || d.preview.s3_srt_url) {
      console.log('✅ isTerminal - Preview has video URL (no full), stopping poll to show preview video');
      return true;
    }
    
    // Nếu preview có error_message -> dừng poll
    if (d.preview.error_message) {
      console.log('❌ isTerminal - Preview has error message (no full), stopping poll');
      return true;
    }
    
    // 🚀 FIX: Nếu preview.status = "completed" nhưng không có full -> TIẾP TỤC poll để đợi full
    if (d.preview.status === 'completed') {
      console.log('⏳ isTerminal - Preview completed but no full data, continue polling for full');
      return false;
    }
  }

  return false;
};

export const useVideoStatus = (videoId?: string, jobId?: string) => {
  const [videoData, setVideoData] = useState<VideoData | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [currentJobId, setCurrentJobId] = useState<string | undefined>(jobId);

  const activeRef = useRef(false);
  const timerRef = useRef<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Clear timer function
  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Stop polling completely
  const stop = useCallback(() => {
    console.log('🛑 useVideoStatus - Stopping polling');
    activeRef.current = false;
    setIsPolling(false);
    clearTimer();
    abortRef.current?.abort();
    abortRef.current = null;
  }, [clearTimer]);

  // Poll once and schedule next if needed
  const pollOnce = useCallback(async () => {
    if (!activeRef.current) {
      console.log('🛑 pollOnce - Polling stopped, skipping');
      return;
    }

    // Cancel previous request if still in flight
    abortRef.current?.abort();
    const abortController = new AbortController();
    abortRef.current = abortController;

    try {
      console.log('🔄 pollOnce - Polling status for:', { videoId, currentJobId });
      
      const progress = await getProgress(videoId!, currentJobId);
      
      // 🚀 Compute derived status for better debugging
      const derivedStatus = computeStatus(progress);
      
      console.log('📡 pollOnce - API response:', {
        raw_status: progress.status,
        full_status: progress.full?.status,
        derivedStatus,
        stage: progress.stage,
        progress: progress.progress,
        isTerminal: isTerminal(derivedStatus),
        isTerminalRaw: isTerminal(progress)
      });

      console.log(`🔍 useVideoStatus - Raw progress object:`, progress);
      
      // Update video data
      setVideoData(prev => {
        if (!prev) {
          // NHÁNH KHỞI TẠO (prev == null)
          const newData = {
            videoId: videoId!,
            jobId: jobId!,
            preview: progress.preview ? {
              status: progress.preview.status,
              progress: progress.preview.progress ?? 0,
              stage: progress.preview.stage || 'processing',
              s3_srt_url: progress.preview.s3_srt_url,
              s3_vtt_url: progress.preview.s3_vtt_url, // 🚀 NEW: Map VTT URL
              s3_video_url: progress.preview.s3_video_url,
              duration: progress.preview.duration,
              error_message: progress.preview.error_message
            } : undefined,
            full: progress.full ? {
              status: progress.full.status,
              progress: progress.full.progress ?? 0,
              stage: progress.full.stage || 'processing',
              job_id: progress.full.job_id,  // 🚀 ADD: Map job_id
              s3_srt_url: progress.full.s3_srt_url,
              s3_vtt_url: progress.full.s3_vtt_url, // 🚀 NEW: Map VTT URL
              s3_video_url: progress.full.s3_video_url,
              duration: progress.full.duration,
              error_message: progress.full.error_message
            } : undefined
          };
          
          console.log(`🚀 useVideoStatus - Creating new videoData:`, newData);
          
          return newData;
        }

        // NHÁNH CẬP NHẬT (prev tồn tại)
        const updatedData = {
          ...prev,
          preview: progress.preview ? {
            status: progress.preview.status,
            progress: progress.preview.progress ?? prev.preview?.progress ?? 0,
            stage: progress.preview.stage || prev.preview?.stage || 'processing',
            s3_srt_url: progress.preview.s3_srt_url,
            s3_vtt_url: progress.preview.s3_vtt_url, // 🚀 NEW: Map VTT URL
            s3_video_url: progress.preview.s3_video_url,
            duration: progress.preview.duration,
            error_message: progress.preview.error_message
          } : prev.preview,
          full: progress.full ? {
            status: progress.full.status,
            progress: progress.full.progress ?? prev.full?.progress ?? 0,
            stage: progress.full.stage || prev.full?.stage || 'processing',
            job_id: progress.full.job_id,  // 🚀 ADD: Map job_id
            s3_srt_url: progress.full.s3_srt_url,
            s3_vtt_url: progress.full.s3_vtt_url, // 🚀 NEW: Map VTT URL
            s3_video_url: progress.full.s3_video_url,
            duration: progress.full.duration,
            error_message: progress.full.error_message
          } : prev.full
        };
        
        // 🚀 FIX: Auto-update currentJobId khi full processing bắt đầu
        if (progress.full?.status === 'processing' && progress.full?.job_id && progress.full.job_id !== currentJobId) {
          console.log('🔄 useVideoStatus - Full processing started, updating currentJobId:', {
            old: currentJobId,
            new: progress.full.job_id
          });
          setCurrentJobId(progress.full.job_id);
        }
        
        return updatedData;
      });

      // 🚀 FIX: Check for error status FIRST before checking terminal status
      if (progress.preview?.status === 'error' || progress.full?.status === 'error') {
        console.log('❌ pollOnce - Error status detected, stopping polling immediately:', {
          preview_status: progress.preview?.status,
          full_status: progress.full?.status,
          preview_error: progress.preview?.error_message,
          full_error: progress.full?.error_message
        });

        // 🚀 SIMPLE: Append error message to DOM when polling stops
        const errorMessage = document.createElement('div');
        errorMessage.className = 'text-center mb-8';
        errorMessage.innerHTML = `
          <div class="bg-red-50 border border-red-200 rounded-lg p-3 max-w-2xl mx-auto">
            <div class="flex items-center justify-between text-red-700">
              <div class="flex items-center space-x-2">
                <svg id="close-icon" class="h-5 w-5 cursor-pointer hover:text-red-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
                <span class="font-medium">Video processing failed. Please try again.</span>
              </div>
              <button id="dismiss-error" class="text-sm text-red-500 hover:text-red-700 underline cursor-pointer">
                Dismiss
              </button>
            </div>
          </div>
        `;
        
        // Add click event listener for dismiss button
        const dismissButton = errorMessage.querySelector('#dismiss-error');
        if (dismissButton) {
          dismissButton.addEventListener('click', () => {
            errorMessage.remove();
          });
        }
        
        // Add click event listener for close icon (X)
        const closeIcon = errorMessage.querySelector('#close-icon');
        if (closeIcon) {
          closeIcon.addEventListener('click', () => {
            errorMessage.remove();
          });
        }
        
        // Insert after statistics section (div flex flex-wrap justify-center gap-6 mb-12 stat-item)
        const statsSection = document.querySelector('.stat-item')?.closest('div');
        if (statsSection && statsSection.parentElement) {
          statsSection.parentElement.insertBefore(errorMessage, statsSection.nextSibling);
        } else {
          // Fallback: insert after first h1 if stats section not found
          const firstH1 = document.querySelector('h1');
          if (firstH1 && firstH1.parentElement) {
            firstH1.parentElement.insertBefore(errorMessage, firstH1.nextSibling);
          }
        }
        
        console.log('🚀 useVideoStatus - Error message appended to DOM');
        
        // STOP IMMEDIATELY - no more scheduling
        stop();
        return;
      }

      // Check if terminal status reached
      if (isTerminal(derivedStatus)) {
        console.log('✅ pollOnce - Terminal status reached, stopping polling:', {
          status: derivedStatus,
          stage: progress.stage,
          progress: progress.progress,
          isTerminal: true
        });
        
        // STOP IMMEDIATELY - no more scheduling
        stop();
        return;
      }

      console.log('⏳ pollOnce - Continuing to poll:', {
        status: derivedStatus,
        stage: progress.stage,
        progress: progress.progress,
        isTerminal: false,
        full_status: progress.full?.status,
        full_job_id: progress.full?.job_id,
        currentJobId: currentJobId,
        preview_status: progress.preview?.status,
        preview_has_url: !!(progress.preview?.s3_video_url || progress.preview?.s3_srt_url)
      });

      // Not terminal yet - schedule next poll
      if (activeRef.current) {
        timerRef.current = window.setTimeout(pollOnce, 2000);
        console.log('⏰ pollOnce - Scheduled next poll in 2s');
      }

    } catch (error: any) {
      if (error?.name === 'AbortError') {
        console.log('🛑 pollOnce - Request aborted');
        return; // Request was cancelled, don't retry
      }
      
      console.error('❌ pollOnce - Error polling status:', error);
      
      // On error, retry with backoff if still active
      if (activeRef.current) {
        timerRef.current = window.setTimeout(pollOnce, 3000);
        console.log('⏰ pollOnce - Scheduled retry in 3s due to error');
      }
    }
  }, [videoId, currentJobId, stop]);

  // Sync currentJobId with jobId prop
  useEffect(() => {
    setCurrentJobId(jobId);
  }, [jobId]);

  // Start polling when IDs change
  useEffect(() => {
    // Reset everything when IDs change
    stop();
    
    if (!videoId || !currentJobId) {
      console.log('🛑 useVideoStatus - No IDs provided, not starting', { videoId, currentJobId });
      return;
    }

    console.log('🚀 useVideoStatus - Starting polling for:', { videoId, currentJobId });
    console.log('🔄 useVideoStatus - Effect triggered by currentJobId change:', currentJobId);
    activeRef.current = true;
    setIsPolling(true);

    // Start first poll immediately
    pollOnce();

    // Cleanup on unmount or ID change
    return stop;
  }, [videoId, currentJobId, pollOnce, stop]);

  // Start full processing
  const startFull = useCallback(async () => {
    if (!videoId) {
      console.error('No video ID available for full processing');
      return;
    }

    try {
      console.log('🚀 useVideoStatus - Starting full processing for:', videoId);
      
      // Check database first before calling RunPod
      console.log('🔍 useVideoStatus - Checking database status first...');
      try {
        const currentStatus = await getProgress(videoId);
        console.log('📊 useVideoStatus - Current database status:', currentStatus);
        
        // If full processing is already completed in database, return the data
        if (currentStatus.full?.status === 'completed') {
          console.log('✅ useVideoStatus - Full processing already completed in database');
          
          setVideoData(prev => ({
            ...prev!,
            full: {
              status: currentStatus.full?.status || 'not_started',
              progress: currentStatus.full?.progress || 0,
              stage: currentStatus.full?.stage || 'initial',
              s3_srt_url: currentStatus.full?.s3_srt_url,
              s3_video_url: currentStatus.full?.s3_video_url,
              duration: currentStatus.full?.duration || 0,
              error_message: currentStatus.full?.error_message
            }
          }));
          
          return; // Don't call RunPod if already completed
        }
        
        // If still processing, update status and continue
        if (currentStatus.full?.status === 'processing') {
          console.log('⏳ useVideoStatus - Full processing still in progress');
          setCurrentJobId(currentStatus.full?.job_id);
          activeRef.current = true;
          setIsPolling(true);
          return;
        }
        
      } catch (dbError) {
        console.log('⚠️ useVideoStatus - Database check failed, proceeding with RunPod:', dbError);
      }
      
      // If not completed in database, proceed with RunPod
      console.log('📤 useVideoStatus - Sending payload to RunPod:', { video_id: videoId });
      const result = await startFullProcessing(videoId);
      
      if (result.job_id) {
        console.log('✅ useVideoStatus - Full processing started, job_id:', result.job_id);
        
        setVideoData(prev => ({
          ...prev!,
          full: {
            status: 'processing',
            progress: 0,
            stage: 'initial',
            s3_srt_url: prev?.full?.s3_srt_url,
            s3_video_url: prev?.full?.s3_video_url,
            duration: prev?.full?.duration || 0,
            error_message: undefined
          }
        }));
        
        // Update currentJobId to use fullJobId for polling
        setCurrentJobId(result.job_id);
        
        // Start polling for full processing
        activeRef.current = true;
        setIsPolling(true);
      }
    } catch (error) {
      console.error('Error starting full processing:', error);
    }
  }, [videoId]);

  return { videoData, startFull, isPolling, stop };
};